<template>
  <section class="bg-surface-1 px-6 lg:px-16 xl:px-40 py-16 md:py-24">
    <div class="flex flex-col lg:flex-row gap-8 lg:gap-12">
      <div class="relative w-full lg:w-[400px] xl:w-[480px] 2xl:w-[585px] shrink-0">
        <div class="relative w-full aspect-[585/700] rounded overflow-hidden bg-black">
          <img
            v-for="m in members" :key="m.key"
            :src="m.imgUrl"
            :alt="t(`home.team.members.${m.key}.name`)"
            class="absolute inset-0 w-full h-full object-cover transition-opacity duration-300"
            :class="m.key === selectedKey ? 'opacity-100' : 'opacity-0'"
          />
        </div>
      </div>

      <div class="hidden lg:block w-px bg-white/10 self-stretch shrink-0" />

      <div class="flex flex-col gap-12 min-w-0 flex-1">
        <div class="flex flex-col gap-8">
          <div class="flex items-start gap-2">
            <div class="bg-primary-dark rounded-[2px] w-3 h-3 mt-[13px] shrink-0" />
            <div class="flex flex-col gap-2">
              <h2 class="font-display font-semibold text-3xl md:text-[56px] leading-tight text-white !mt-0 !mb-0">
                {{ t('home.team.title') }}
              </h2>
              <p class="font-sans text-base md:text-lg text-[#a1a4a6] !mt-0 !mb-0">
                {{ t('home.team.subtitle') }}
              </p>
            </div>
          </div>

          <div class="grid grid-cols-3 gap-3 md:gap-4">
            <button
              v-for="m in members" :key="m.key"
              type="button"
              :aria-label="t(`home.team.members.${m.key}.name`)"
              :aria-pressed="m.key === selectedKey"
              class="relative aspect-square rounded overflow-hidden transition-all"
              :class="m.key === selectedKey ? 'ring-2 ring-primary' : 'opacity-60 hover:opacity-100'"
              @click="selectedKey = m.key"
            >
              <img :src="m.imgUrl" :alt="''" aria-hidden="true" class="absolute inset-0 w-full h-full object-cover" />
              <div v-if="m.key !== selectedKey" class="absolute inset-0 bg-black/40 pointer-events-none" />
            </button>
          </div>
        </div>

        <div class="flex flex-col gap-6">
          <div class="flex items-center justify-between gap-4">
            <div class="flex flex-col gap-1 min-w-0">
              <h3 class="font-display font-semibold text-2xl md:text-3xl text-white !mt-0 !mb-0">
                {{ t(`home.team.members.${selectedKey}.name`) }}
              </h3>
              <p class="font-sans text-base text-[#a1a4a6] !mt-0 !mb-0">
                {{ t(`home.team.members.${selectedKey}.role`) }}
              </p>
            </div>
            <button
              type="button"
              :aria-label="t('home.team.next')"
              class="inline-flex items-center justify-center w-11 h-11 rounded bg-primary hover:bg-primary-dark text-white transition-colors shrink-0"
              @click="cycleNext"
            >
              <VaIcon name="arrow_forward" :size="22" />
            </button>
          </div>
          <p class="font-sans text-base md:text-lg text-[#a1a4a6] !mt-0 !mb-0">
            {{ t(`home.team.members.${selectedKey}.bio`) }}
          </p>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { VaIcon } from 'vuestic-ui'
import stefanUrl from '@/assets/images/portrait-stefan.webp?url'
import sanyaUrl from '@/assets/images/portrait-sanya.webp?url'
import svetoslavUrl from '@/assets/images/portrait-svetoslav.webp?url'

const { t } = useI18n()

const members = [
  { key: 'stefan', imgUrl: stefanUrl },
  { key: 'sanya', imgUrl: sanyaUrl },
  { key: 'svetoslav', imgUrl: svetoslavUrl },
] as const

const selectedKey = ref<typeof members[number]['key']>('stefan')

const cycleNext = () => {
  const idx = members.findIndex(m => m.key === selectedKey.value)
  selectedKey.value = members[(idx + 1) % members.length].key
}
</script>
